import { useCallback, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { updatePreferences, type ISessionPreferences } from '../../entities/session';
import { applyTheme, type TTheme } from './theme';

/** `timezone: null` is "follow this browser", the state of an account nobody set a zone for. */
export interface IAccountDraft {
  theme: TTheme;
  timezone: string | null;
}

const draftOf = (preferences: ISessionPreferences): IAccountDraft => ({
  theme: preferences.theme,
  timezone: preferences.zoneChosen ? preferences.timezone : null,
});

// Display only. The notification matrix that used to live here is on
// Settings -> Notifications, next to the organization's own switches.
export const useAccountPreferences = (preferences: ISessionPreferences) => {
  const client = useQueryClient();
  const saved = useMemo(() => draftOf(preferences), [preferences]);
  const [draft, setDraft] = useState<IAccountDraft>(saved);
  const [edited, setEdited] = useState(false);

  const current = edited ? draft : saved;

  const change = useCallback((next: Partial<IAccountDraft>) => {
    setDraft((previous) => ({ ...previous, ...next }));
    setEdited(true);
  }, []);

  const dirty = current.theme !== saved.theme || current.timezone !== saved.timezone;

  const save = useMutation({
    mutationFn: () => updatePreferences({ theme: current.theme, timezone: current.timezone }),
    onSuccess: async () => {
      applyTheme(current.theme);
      setEdited(false);
      await client.invalidateQueries({ queryKey: ['session'] });
    },
  });

  return { draft: current, change, save, dirty };
};

export type TAccountPreferences = ReturnType<typeof useAccountPreferences>;
